import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiGet } from '../../services/api'
import { Users, FileText, Clock, TrendingUp, Calendar, Trophy, Activity, ArrowUp, ArrowDown, Target, ChevronRight, Info } from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { usePermissions } from '../../hooks/usePermissions'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts'

interface DashboardSummary {
  total_users: number
  total_supervisors: number
  monthly_submissions: number
  pending_reviews: number
  today_submissions: number
  avg_score: number
}

interface IndicatorStat {
  indicator: {
    id: number
    code: string
    name: string
    target: number
    max_points: number
  }
  total_submissions: number
  avg_submissions: number
  avg_score: number
  target_achievement: number
}

interface DashboardData {
  period: {
    id: number
    year: number
    month: number
    name: string
  }
  indicator_stats: IndicatorStat[]
  score_distribution: Record<string, number>
  total_supervisors: number
}

interface TrendData {
  date: string
  count: number
  day: string
}

interface Performer {
  rank: number
  user: {
    id: number
    name: string
    employee_id: string
    avatar: string
  }
  score: number
  submissions: number
  is_eligible: boolean
}

interface RecentActivity {
  id: number
  type: string
  user: { id: number; name: string; avatar: string }
  indicator: { id: number; code: string; name: string } | null
  status: string
  submission_date: string
  created_at: string
  notes: string | null
}

interface DeadlineStatus {
  period: { id: number; name: string; submission_deadline: string }
  deadline: string | null
  days_remaining: number | null
  is_overdue: boolean
  progress: {
    total_supervisors: number
    active_submitters: number
    participation_rate: number
    total_submissions: number
    completion_rate: number
  }
}

interface PeriodComparison {
  current: {
    period: { name: string }
    total_submissions: number
    avg_score: number
    eligible_count: number
    total_supervisors: number
    eligibility_rate: number
  } | null
  previous: {
    period: { name: string }
    total_submissions: number
    avg_score: number
    eligibility_rate: number
  } | null
  comparison: {
    submissions_delta: number
    submissions_pct: number
    score_delta: number
    eligibility_delta: number
  } | null
}

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6']
const INDICATOR_COLORS = ['#3b82f6', '#22c55e', '#a855f7', '#f97316', '#ec4899', '#06b6d4', '#f59e0b', '#6366f1']

export default function AdminDashboardPage() {
  const { hasModule } = usePermissions()
  const kpiEnabled = hasModule('kpi')
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [trends, setTrends] = useState<TrendData[]>([])
  const [topPerformers, setTopPerformers] = useState<Performer[]>([])
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [deadlineStatus, setDeadlineStatus] = useState<DeadlineStatus | null>(null)
  const [periodComparison, setPeriodComparison] = useState<PeriodComparison | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (kpiEnabled) {
          const [summaryRes, dashboardRes, trendsRes, performersRes, activityRes, deadlineRes, comparisonRes] = await Promise.all([
            apiGet<DashboardSummary>('/reports/dashboard/summary'),
            apiGet<DashboardData>('/reports/dashboard').catch(() => null),
            apiGet<{ trends: TrendData[] }>('/reports/dashboard/trends?days=14'),
            apiGet<{ performers: Performer[] }>('/reports/dashboard/top-performers?limit=5'),
            apiGet<{ activities: RecentActivity[] }>('/reports/dashboard/recent-activity?limit=10'),
            apiGet<DeadlineStatus>('/reports/dashboard/deadline-status'),
            apiGet<PeriodComparison>('/reports/dashboard/period-comparison')
          ])

          setSummary(summaryRes)
          if (dashboardRes) {
            setDashboardData(dashboardRes)
          }
          setTrends(trendsRes.trends || [])
          setTopPerformers(performersRes.performers || [])
          setRecentActivity(activityRes.activities || [])
          setDeadlineStatus(deadlineRes)
          setPeriodComparison(comparisonRes)
        }
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchDashboardData()
  }, [kpiEnabled])

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
  }

  // Prepare pie chart data from score distribution
  const scoreDistributionData = dashboardData?.score_distribution
    ? Object.entries(dashboardData.score_distribution)
        .map(([range, count]) => ({ name: range, value: count }))
        .filter(item => item.value > 0)
    : []

  // Prepare bar chart data for KPI indicators
  const indicatorChartData = dashboardData?.indicator_stats.map(stat => ({
    name: stat.indicator.code,
    fullName: stat.indicator.name,
    achievement: stat.target_achievement,
    avgScore: stat.avg_score,
    maxPoints: stat.indicator.max_points
  })) || []

  // Format trends data for display
  const formattedTrends = trends.map(t => ({
    ...t,
    dateLabel: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        {dashboardData?.period && (
          <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
            {dashboardData.period.name}
          </span>
        )}
      </div>

      {/* KPI disabled info card */}
      {!kpiEnabled && (
        <div className="card border border-blue-200 bg-blue-50">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900">KPI Module Disabled</h3>
              <p className="text-sm text-blue-700 mt-1">
                KPI & Scoring module is disabled for this company. Enable it from the superadmin panel to see KPI analytics and reports.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats Cards */}
      {kpiEnabled && (<>
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Users</p>
              <p className="text-xl font-bold">{summary?.total_users || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Supervisors</p>
              <p className="text-xl font-bold">{summary?.total_supervisors || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Monthly Submissions</p>
              <p className="text-xl font-bold">{summary?.monthly_submissions || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Pending Review</p>
              <p className="text-xl font-bold">{summary?.pending_reviews || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Calendar className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Today</p>
              <p className="text-xl font-bold">{summary?.today_submissions || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Avg Score</p>
              <p className="text-xl font-bold">{summary?.avg_score || 0}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Deadline & Period Comparison Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deadline Status Card */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Target className="h-5 w-5 text-primary-600" />
              Deadline Status
            </h3>
            {deadlineStatus?.deadline && (
              <span className={`text-sm px-2 py-1 rounded-full ${
                deadlineStatus.is_overdue
                  ? 'bg-red-100 text-red-700'
                  : deadlineStatus.days_remaining !== null && deadlineStatus.days_remaining <= 3
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-green-100 text-green-700'
              }`}>
                {deadlineStatus.is_overdue
                  ? 'Overdue'
                  : deadlineStatus.days_remaining !== null
                    ? `${deadlineStatus.days_remaining} days left`
                    : 'No deadline'}
              </span>
            )}
          </div>

          {deadlineStatus?.progress ? (
            <div className="space-y-4">
              {/* Progress bars */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Participation</span>
                  <span className="font-medium">{deadlineStatus.progress.active_submitters}/{deadlineStatus.progress.total_supervisors} supervisors</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(deadlineStatus.progress.participation_rate, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{deadlineStatus.progress.participation_rate}% have submitted</p>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Completion Rate</span>
                  <span className="font-medium">{deadlineStatus.progress.total_submissions} submissions</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-green-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(deadlineStatus.progress.completion_rate, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{deadlineStatus.progress.completion_rate}% of expected target</p>
              </div>

              {deadlineStatus.deadline && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm text-gray-600">
                    Deadline: <span className="font-medium">{new Date(deadlineStatus.deadline).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No active period found
            </div>
          )}
        </div>

        {/* Period Comparison Card */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary-600" />
              Period Comparison
            </h3>
          </div>

          {periodComparison?.current ? (
            <div className="space-y-4">
              {/* Comparison metrics */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Submissions</p>
                  <p className="text-xl font-bold">{periodComparison.current.total_submissions}</p>
                  {periodComparison.comparison && (
                    <p className={`text-xs flex items-center justify-center gap-1 ${
                      periodComparison.comparison.submissions_delta >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {periodComparison.comparison.submissions_delta >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                      {Math.abs(periodComparison.comparison.submissions_pct)}%
                    </p>
                  )}
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Avg Score</p>
                  <p className="text-xl font-bold">{periodComparison.current.avg_score}</p>
                  {periodComparison.comparison && (
                    <p className={`text-xs flex items-center justify-center gap-1 ${
                      periodComparison.comparison.score_delta >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {periodComparison.comparison.score_delta >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                      {Math.abs(periodComparison.comparison.score_delta)} pts
                    </p>
                  )}
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Eligibility</p>
                  <p className="text-xl font-bold">{periodComparison.current.eligibility_rate}%</p>
                  {periodComparison.comparison && (
                    <p className={`text-xs flex items-center justify-center gap-1 ${
                      periodComparison.comparison.eligibility_delta >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {periodComparison.comparison.eligibility_delta >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                      {Math.abs(periodComparison.comparison.eligibility_delta)}%
                    </p>
                  )}
                </div>
              </div>

              {/* Period names */}
              <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
                <div>
                  <p className="text-gray-500">Current</p>
                  <p className="font-medium">{periodComparison.current.period.name}</p>
                </div>
                {periodComparison.previous && (
                  <div className="text-right">
                    <p className="text-gray-500">Previous</p>
                    <p className="font-medium">{periodComparison.previous.period.name}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No period data available
            </div>
          )}
        </div>
      </div>

      {/* Top Performers & Recent Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers Leaderboard */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Top Performers
            </h3>
            <Link to="/admin/reports" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {topPerformers.length > 0 ? (
            <div className="space-y-3">
              {topPerformers.map((performer, idx) => (
                <div
                  key={performer.user.id}
                  className={`flex items-center gap-3 p-2 rounded-lg ${idx === 0 ? 'bg-yellow-50' : idx === 1 ? 'bg-gray-50' : idx === 2 ? 'bg-orange-50' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                    idx === 1 ? 'bg-gray-300 text-gray-700' :
                    idx === 2 ? 'bg-orange-300 text-orange-800' :
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {performer.rank}
                  </div>
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-medium text-sm">
                    {performer.user.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{performer.user.name}</p>
                    <p className="text-xs text-gray-500">{performer.submissions} submissions</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${performer.is_eligible ? 'text-green-600' : 'text-gray-700'}`}>
                      {performer.score}
                    </p>
                    <p className="text-xs text-gray-500">points</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No submission data yet
            </div>
          )}
        </div>

        {/* Recent Activity Feed */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-500" />
              Recent Activity
            </h3>
          </div>

          {recentActivity.length > 0 ? (
            <div className="space-y-3 max-h-[320px] overflow-y-auto">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-xs flex-shrink-0">
                    {activity.user.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium text-gray-900">{activity.user.name}</span>
                      <span className="text-gray-500"> submitted </span>
                      {activity.indicator && (
                        <span className="font-medium text-primary-600">{activity.indicator.code}</span>
                      )}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        activity.status === 'approved' ? 'bg-green-100 text-green-700' :
                        activity.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {activity.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {activity.created_at && new Date(activity.created_at).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No recent activity
            </div>
          )}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Submission Trends Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Submission Trends (Last 14 Days)</h3>
          {formattedTrends.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={formattedTrends}>
                <defs>
                  <linearGradient id="colorSubmissions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                  formatter={(value: number) => [value, 'Submissions']}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#colorSubmissions)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No trend data available
            </div>
          )}
        </div>

        {/* Score Distribution Pie Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Score Distribution</h3>
          {scoreDistributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={scoreDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                >
                  {scoreDistributionData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number) => [`${value} supervisors`, 'Count']}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No score data available
            </div>
          )}
          {scoreDistributionData.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {scoreDistributionData.map((item, index) => (
                <div key={item.name} className="flex items-center gap-1 text-xs">
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span>{item.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* KPI Performance Chart */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">KPI Indicator Performance</h3>
        {indicatorChartData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={indicatorChartData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fontWeight: 500 }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  width={50}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === 'achievement') return [`${value.toFixed(1)}%`, 'Target Achievement']
                    return [value, name]
                  }}
                  labelFormatter={(label) => {
                    const item = indicatorChartData.find(d => d.name === label)
                    return item?.fullName || label
                  }}
                />
                <Bar
                  dataKey="achievement"
                  radius={[0, 4, 4, 0]}
                  maxBarSize={30}
                >
                  {indicatorChartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={INDICATOR_COLORS[index % INDICATOR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Indicator Legend */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-100">
              {dashboardData?.indicator_stats.map((stat, index) => (
                <div key={stat.indicator.id} className="flex items-start gap-2">
                  <div
                    className="w-3 h-3 rounded mt-1 flex-shrink-0"
                    style={{ backgroundColor: INDICATOR_COLORS[index % INDICATOR_COLORS.length] }}
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{stat.indicator.code}</p>
                    <p className="text-xs text-gray-500 truncate">{stat.indicator.name}</p>
                    <p className="text-xs text-gray-400">
                      Avg: {stat.avg_score}/{stat.indicator.max_points} pts
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-500">
            No KPI data available
          </div>
        )}
      </div>
      </>)}

    </div>
  )
}
